import React, { useState, useMemo, useEffect } from 'react';
import {
    Container,
    Paper,
    TextField,
    Button,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormHelperText,
    Modal,
    Typography
} from '@mui/material';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 400,
    width: "80%",
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
    };


const ShiftChangeRequestForm = ({ 
    employeeCode, position, name, shiftChanges, shiftOptionList, handleSubmitShiftChangeRequst,
    shiftSubmitPassword, setPasswordPopupOpen, shiftChangeLimitEmps
 }) => {

    const initialFormData = useMemo(() => ({
        employeeId: employeeCode,
        department: position,
        name: name,
        shiftPattern: '',
        absentDate: '',
        alternativeDate: '',
        reason: ''
    }), [employeeCode, position, name]);

    // モーダル用
    const [ open, setOpen ] = useState(false);
    const handleClose = () => setOpen(false);
    const [ submitAvailable, setSubmitAvailable ] = useState(false);


    const checkShiftChangeValidation = () => {

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const absentDate = new Date(formData.absentDate);
        const alternativeDate = new Date(formData.alternativeDate);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        // 2週間後の日付を計算
        const twoWeeksFromToday = new Date(today);
        twoWeeksFromToday.setDate(twoWeeksFromToday.getDate() + 14);

        const newErrors = {};

        if (!formData.shiftPattern) {
            newErrors.shiftPattern = '出勤時間を選択してください';
        }

        if (absentDate < today) {
            newErrors.absentDate = '本日以降の日付を選択してください';
        } else if (absentDate > twoWeeksFromToday) {
            newErrors.absentDate = '2週間以内の日付のみ選択できます';
        }

        if (!formData.alternativeDate) {
            newErrors.alternativeDate = '代替日を選択してください';
        } else if (alternativeDate < today) {
            newErrors.alternativeDate = '本日以降の日付を選択してください';
        } else if (alternativeDate > twoWeeksFromToday) {
            newErrors.alternativeDate = '2週間以内の日付のみ選択できます';
        } else if (alternativeDate.getMonth() !== absentDate.getMonth()) {
            newErrors.alternativeDate = '同じ月内の日付を選択してください';
        } else if (formData.alternativeDate === formData.absentDate) {
            newErrors.alternativeDate = '欠勤希望日と異なる日付を選択してください';
        }

        if (!formData.reason.trim()) {
            newErrors.reason = '欠勤理由を入力してください';
        }
        
        if (shiftChangeLimitEmps.includes(employeeCode)) {
            newErrors.employeeId = 'シフト変更制限が掛けられています';
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            handleOpen();
        };
    }

    const handleOpen = () => {
        if (!shiftSubmitPassword || shiftSubmitPassword === "") {
            setPasswordPopupOpen(true);
        } else {
            setOpen(true);
        }
    }

    const handleChangeForPassword = (e) => {
        const inputtedPassword = e.target.value;
        if (
            (inputtedPassword === shiftSubmitPassword) ||
            (employeeCode.includes("A") && inputtedPassword === "dz-shift-sp") ||
            (employeeCode.includes("B") && inputtedPassword === "dz-tc-shift") ||
            (employeeCode.includes("S") && inputtedPassword === "sd-dz-shift") ||
            (employeeCode.includes("T") && inputtedPassword === "dz-shift_tk")
        ) {
            setSubmitAvailable(true);
        } else {
            setSubmitAvailable(false);
        }
    }

    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});


    useEffect(() => {
        setFormData(initialFormData);
        setErrors({
            shiftPattern: '',
            absentDate: '',
            alternativeDate: '',
            reason: ''
        });
    }, [initialFormData]);


    const handleChange = (e) => {
        const { name, value } = e.target;
        
        setFormData(prev => {
            // shiftPatternが変更された場合、alternativeShiftPatternも同時に更新
            if (name === 'shiftPattern') {
                return {
                    ...prev,
                    [name]: value,
                };
            }
            return {
                ...prev,
                [name]: value
            };
        });

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        try {
            e.preventDefault();

            const today = new Date();
            const absentDate = new Date(formData.absentDate);

            let timeKey = "current";
            let shift_changes = shiftChanges[timeKey];
            if (today.getMonth() !== absentDate.getMonth()) {
                timeKey = "next"
                shift_changes = shiftChanges[timeKey];
            }
            
            const year = absentDate.getFullYear();
            const month = absentDate.getMonth() + 1;
            const selectedDate = absentDate.getDate();

            const shiftPatternLetter = formData.shiftPattern.split(":")[0];

            const submitDataForFirebase = {
                "employee_code": formData.employeeId,
                "location_key": formData.employeeId[0],
                "year": year,
                "month": month,
                "position": position,
                "shift_changes": [
                    ...shift_changes["shift_changes"],
                    {
                        "date": selectedDate,
                        "shift_pattern": shiftPatternLetter,
                        "before": formData.absentDate,
                        "after": formData.alternativeDate,
                        "reason": formData.reason,
                        "updated_at": new Date()
                    }
                ]
            }
            const shiftChangeDataForGChat = {
                "employee_code": formData.employeeId,
                "name": name,
                "location_key": formData.employeeId[0],
                "position": position,
                "absent_date": formData.absentDate,
                "alternative_date": formData.alternativeDate,
                "shift_pattern": formData.shiftPattern,
                "reason": formData.reason
            }
            setSubmitAvailable(false);
            await handleSubmitShiftChangeRequst(submitDataForFirebase, shiftChangeDataForGChat, timeKey);
            handleClose();
            alert("シフト変更申請が完了しました。")
            
        } catch (error) {
            setSubmitAvailable(false);
            alert("シフト変更申請に失敗しました。時間をおいて再度試すか、管理者へ問い合わせてください。")
        }
    };

    return (
        <>
                <Container maxWidth="sm">
                    <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
                        <TextField
                            margin="normal"
                            fullWidth
                            label="従業員ID"
                            name="employeeId"
                            value={formData.employeeId}
                            onChange={handleChange}
                            disabled
                            size="small"
                            error={!!errors.employeeId}
                            helperText={errors.employeeId}
                        />
                        <TextField
                            margin="normal"
                            fullWidth
                            label="所属"
                            name="department"
                            value={formData.department}
                            onChange={handleChange}
                            size="small"
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                        <TextField
                            margin="normal"
                            fullWidth
                            label="欠勤希望日"
                            type="date"
                            name="absentDate"
                            value={formData.absentDate}
                            onChange={handleChange}
                            size="small"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            error={!!errors.absentDate}
                            helperText={errors.absentDate}
                        />
                        <FormControl 
                            fullWidth 
                            margin="normal"
                            error={!!errors.shiftPattern}
                            size="small"
                        >
                            <InputLabel id="shift-pattern-label">欠勤日の出勤時間</InputLabel>
                            <Select
                                labelId="shift-pattern-label"
                                name="shiftPattern"
                                value={formData.shiftPattern || ''}
                                onChange={handleChange}
                                label="欠勤日の出勤時間"
                            >
                                {shiftOptionList.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.shiftPattern && (
                                <FormHelperText>{errors.shiftPattern}</FormHelperText>
                            )}
                        </FormControl>
                        <TextField
                            margin="normal"
                            fullWidth
                            label="代替日"
                            type="date"
                            name="alternativeDate"
                            value={formData.alternativeDate}
                            onChange={handleChange}
                            size="small"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            error={!!errors.alternativeDate}
                            helperText={errors.alternativeDate}
                        />
                        <FormControl 
                            fullWidth 
                            margin="normal"
                            error={!!errors.shiftPattern}
                            size="small"
                        >
                            <InputLabel id="alternative-shift-pattern-label">代替日の出勤時間</InputLabel>
                            <Select
                                labelId="alternative-shift-pattern-label"
                                name="alternativeShiftPattern"
                                value={formData.shiftPattern}
                                onChange={handleChange}
                                label="代替日の出勤時間"
                                disabled // 欠勤日の出勤時間と同期するため無効化
                            >
                                {shiftOptionList.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            margin="normal"
                            fullWidth
                            label="欠勤理由"
                            name="reason"
                            value={formData.reason}
                            onChange={handleChange}
                            multiline
                            rows={4}
                            size="small"
                            error={!!errors.reason}
                            helperText={errors.reason}
                        />
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                onClick={checkShiftChangeValidation}
                            >
                                申請
                            </Button>
                        </Box>
                    </Paper>
                </Container>
                <Modal
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                >
                <Box sx={style}>
                    <>
                        <Typography id="modal-modal-title" variant="p" component="h5">
                            シフト変更申請をします。<br/>
                            パスワードを入力して「OK」ボタンを押してください。
                            <Typography fontSize={12}>
                            パスワードを忘れた場合は管理者へ問い合わせてください。
                            </Typography>
                        </Typography>
                        <TextField
                        id="password-input" 
                        label="Password"
                        type="password"
                        size='small'
                        sx={{
                            my:2
                        }}
                        onChange={handleChangeForPassword}
                        />

                        <Container 
                            sx={{
                                width: "100%", 
                                display: "flex", 
                                justifyContent: "space-between", 
                                my: 1
                            }}
                        >
                            <Button 
                                disabled={!submitAvailable}
                                variant="outlined"
                                sx={{
                                    width: "150px"
                                }}
                                onClick={handleSubmit}
                            >
                                OK
                            </Button>
                            <Button
                                color='error'
                                variant="outlined"
                                sx={{
                                    width: "150px"
                                }}
                                onClick={handleClose}
                            >
                                キャンセル
                            </Button>
                        </Container>
                    </>
                </Box>
            </Modal>
        </>
    );
};

export default ShiftChangeRequestForm;